import { useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { ReactSortable } from 'react-sortablejs';
import styled, { useTheme } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import DateTimePicker from 'components/DateTimePicker';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import {
  getHighestItemOrderNumber,
  getOrderedItems,
} from 'components/Form/DraggableFields';
import { Select as FormFieldSelect } from 'components/FormFields';
import AttachmentUpload from 'components/FormFields/AttachmentUpload';
import ColorSelect from 'components/FormFields/ColorSelect';
import SearchableSelect from 'components/FormFields/SearchableSelect';
import Toggle from 'components/FormFields/Toggle';
import Toaster from 'components/Toaster';
import Tooltip from 'components/Tooltip';
import { COLOR_CODES } from 'pages/app/Events/EventEdit/EditEventTickets';
import { GET_WAIVERS } from 'pages/app/Events/EventEdit/WaiversRepeater/Waivers.queries';
import { useGetAllCategories } from 'pages/app/Events/gql/queries/useGetAllCategories';
import { sortWaivers } from 'pages/app/Waivers/sortWaivers';
import { sortTiersByPriceDesc, toNullablePrice } from 'shared/utils/price';
import { useUpdateEventTicket } from '../../CreateEventV2/hooks/useUpdateEventTicket';
import { getDefaultTicketFees, getDefaultPosFees } from '../AddEventTicket';
import BundleTicketsSection from '../AddEventTicket/BundleTicketsSection';

// Query to get event with all ticket arrays
const GET_EVENT_WITH_TICKETS = gql`
  query GetEventWithTickets($input: GetEventInput!) {
    getEvent(input: $input) {
      id
      name
      account_id
      start_date
      end_date
      eventDates
      reserved_seats
      sales_windows
      admin_tickets {
        id
        name
        price
        track_price
        description
        description_image_id
        description_image
        description_video
        start_date
        end_date
        color_code
        fee_dollar
        fee_percent
        pos_fee_dollar
        pos_fee_percent
        limit
        visibility
        is_featured
        featured_text
        category_id
        reserved_seat
        reserved_seat_category
        reserved_seat_prefix
        reserved_seat_section
        chart_id
        order
        sale_start
        sale_end
        on_sale_time
        off_sale_time
        windows {
          id
          name
          ends
          time
          price
          tiers {
            id
            name
            price
            track_price
            color_code
          }
        }
        tiers {
          id
          name
          price
          track_price
          color_code
        }
      }
      admin_multiday_tickets {
        id
        name
        price
        track_price
        description
        description_image_id
        description_image
        description_video
        start_date
        end_date
        color_code
        fee_dollar
        fee_percent
        pos_fee_dollar
        pos_fee_percent
        limit
        visibility
        is_featured
        featured_text
        category_id
        reserved_seat
        reserved_seat_category
        reserved_seat_prefix
        reserved_seat_section
        chart_id
        order
        sale_start
        sale_end
        on_sale_time
        off_sale_time
        windows {
          id
          name
          ends
          time
          price
          tiers {
            id
            name
            price
            track_price
            color_code
          }
        }
        tiers {
          id
          name
          price
          track_price
          color_code
        }
      }
      admin_other_tickets {
        id
        name
        price
        track_price
        description
        description_image_id
        description_image
        description_video
        start_date
        end_date
        color_code
        fee_dollar
        fee_percent
        pos_fee_dollar
        pos_fee_percent
        limit
        visibility
        is_featured
        featured_text
        category_id
        reserved_seat
        reserved_seat_category
        reserved_seat_prefix
        reserved_seat_section
        chart_id
        order
        sale_start
        sale_end
        on_sale_time
        off_sale_time
        windows {
          id
          name
          ends
          time
          price
          tiers {
            id
            name
            price
            track_price
            color_code
          }
        }
        tiers {
          id
          name
          price
          track_price
          color_code
        }
      }
      admin_bundle_tickets {
        id
        name
        price
        track_price
        description
        description_image_id
        description_image
        description_video
        start_date
        end_date
        color_code
        fee_dollar
        fee_percent
        pos_fee_dollar
        pos_fee_percent
        limit
        visibility
        is_featured
        featured_text
        category_id
        reserved_seat
        reserved_seat_category
        reserved_seat_prefix
        reserved_seat_section
        chart_id
        order
        sale_start
        sale_end
        on_sale_time
        off_sale_time
        associated_tickets {
          id
          name
          quantity
        }
        windows {
          id
          name
          ends
          time
          price
          tiers {
            id
            name
            price
            track_price
            color_code
          }
        }
        tiers {
          id
          name
          price
          track_price
          color_code
        }
      }
      admin_registration_tickets {
        id
        name
        price
        track_price
        description
        description_image_id
        description_image
        description_video
        start_date
        end_date
        color_code
        fee_dollar
        fee_percent
        pos_fee_dollar
        pos_fee_percent
        limit
        visibility
        is_featured
        featured_text
        category_id
        reserved_seat
        reserved_seat_category
        reserved_seat_prefix
        reserved_seat_section
        set_member_number
        prefix_text
        suffix_text
        last_member_number
        chart_id
        order
        sale_start
        sale_end
        on_sale_time
        off_sale_time
        admin_pages {
          id
          pdf_id
          pdf_url
          name
          form_id
          waivers
          order
          video_url
        }
        windows {
          id
          name
          ends
          time
          price
          tiers {
            id
            name
            price
            track_price
            color_code
          }
        }
        tiers {
          id
          name
          price
          track_price
          color_code
        }
      }
    }
  }
`;

// Query to fetch seating charts
const GET_SEATING_CHARTS = gql`
  query GetSeatingCharts {
    getSeatingCharts {
      id
      name
    }
  }
`;

// GraphQL query for searching Formstack forms
const SEARCH_FORMSTACK_FORMS = gql`
  query SearchFormstackForms($queryString: String!) {
    searchFormstackForms(queryString: $queryString) {
      form_id
      name
    }
  }
`;

// Page styling
const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme?.colors?.background?.primary ||
    (theme?.name === 'dark' ? '#1a1a2e' : '#f8fafc')};
  font-family: ${({ theme }) =>
    theme?.fonts?.primary ||
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'};
`;

const Container = styled.div`
  padding: 30px;
  max-width: 1100px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeaderLeft = styled.div``;

const HeaderRight = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    justify-content: flex-end;
  }
`;

const BackButtonWrapper = styled.div`
  margin-bottom: 16px;

  button {
    width: auto;
    padding: 0;
    background: none;
    border: none;
    box-shadow: none;

    &:hover {
      background: none;
      box-shadow: none;
    }
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  margin: 0;
  line-height: 1.6;
`;

const HeaderButtonWrapper = styled.div`
  button {
    width: auto;
    padding: 10px 24px;
  }
`;

// Form Card
const FormCard = styled.div`
  background: ${({ theme }) =>
    theme?.colors?.card?.background ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)')};
  border: 1px solid
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
`;

const FormSection = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SectionDescription = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.muted};
  margin: -8px 0 24px 0;
  line-height: 1.5;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${({ columns }) => columns || '1fr'};
  gap: 16px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div``;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textGray.dark};
  margin-bottom: 8px;
`;

const LabelWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textGray.dark};
  margin-bottom: 8px;
`;

const RequiredBadge = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.status.error};
  margin-left: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: ${({ theme }) =>
    theme.colors.input?.background || theme.colors.card.background};
  border: 1px solid
    ${({ theme, error }) =>
      error ? theme.colors.status.error : theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: inherit;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primaryOpacity(0.1)};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textGray.muted};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  background: ${({ theme }) =>
    theme.colors.input?.background || theme.colors.card.background};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: inherit;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all 0.2s;
  min-height: 80px;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primaryOpacity(0.1)};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textGray.muted};
  }
`;

const ErrorText = styled.span`
  display: block;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.status.error};
  margin-top: 6px;
`;

// Toggle Components
const ToggleCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: ${({ theme }) => theme.colors.background.overlay};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const ToggleInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ToggleTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ToggleDescription = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.card.border};

  &:last-child {
    border-bottom: none;
  }
`;

// Two column grid
const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#94a3b8' : '#64748b')};
  font-size: 16px;
`;

const NotFoundContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#94a3b8' : '#64748b')};
  font-size: 16px;
  gap: 16px;
`;

// Registration Pages
const PagesContainer = styled.div`
  display: flex;
  gap: 40px;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const PagesListSection = styled.div`
  flex: 2;
`;

const PagesOrderSection = styled.div`
  flex: 1;
  min-width: 250px;

  @media (max-width: 900px) {
    order: -1;
  }
`;

const PageCard = styled.div`
  background: ${({ theme }) => theme.colors.background.overlay};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 20px;
  margin-bottom: 16px;
  position: relative;
`;

const PageCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const PageCardTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textGray.dark};
`;

const DeletePageButtonWrapper = styled.div`
  button {
    width: 36px;
    height: 36px;
    padding: 0;
  }
`;

const AddPageButtonWrapper = styled.div`
  button {
    width: 100%;
    padding: 14px 24px;
  }
`;

const EmptyPagesMessage = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.muted};
  text-align: center;
  padding: 40px 24px;
  margin: 0 0 16px 0;
  background: ${({ theme }) => theme.colors.background.overlay};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px dashed ${({ theme }) => theme.colors.card.border};
`;

const OrderSectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DraggablePageItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.card.background};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: 8px;
  cursor: move;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const DragHandle = styled.div`
  color: ${({ theme }) => theme.colors.textGray.muted};
  display: flex;
  align-items: center;
`;

const PageOrderName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textGray.dark};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const WaiversContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EmptyText = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textGray.muted};
`;

// Sales Windows
const SalesWindowsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SalesWindowCard = styled.div`
  background: ${({ theme }) => theme.colors.background.overlay};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 16px;
  position: relative;
`;

const SalesWindowHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SalesWindowTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textGray.dark};
`;

const SmallIconButtonWrapper = styled.div`
  button {
    width: 32px;
    height: 32px;
    padding: 0;
  }
`;

const AddWindowButtonWrapper = styled.div`
  button {
    width: 100%;
    padding: 12px 20px;
    border-style: dashed;
  }
`;

const EmptyWindowsMessage = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.muted};
  text-align: center;
  padding: 24px;
  margin: 0;
`;

// Tiered Pricing
const TierSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.card.border};
`;

const TierHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const TierTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AddTierButtonWrapper = styled.div`
  button {
    width: auto;
    padding: 6px 12px;
    font-size: 13px;
  }
`;

const TierRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr auto;
  gap: 12px;
  align-items: end;
  margin-bottom: 8px;
`;

const EmptyTiersMessage = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textGray.muted};
  text-align: center;
  padding: 16px;
  margin: 0;
`;

const RemoveTierButtonWrapper = styled.div`
  button {
    width: 32px;
    height: 32px;
    padding: 0;
  }
`;

const WaiverRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  > div:first-child {
    flex: 1;
  }
`;

const RemoveWaiverButtonWrapper = styled.div`
  button {
    width: 28px;
    height: 28px;
    padding: 0;
  }
`;

const AddWaiverButtonWrapper = styled.div`
  margin-top: 8px;

  button {
    width: auto;
    padding: 8px 16px;
    font-size: 13px;
  }
`;

// Icons
const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const MinusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const DragHandleIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="5" cy="4" r="1" fill="currentColor" />
    <circle cx="11" cy="4" r="1" fill="currentColor" />
    <circle cx="5" cy="8" r="1" fill="currentColor" />
    <circle cx="11" cy="8" r="1" fill="currentColor" />
    <circle cx="5" cy="12" r="1" fill="currentColor" />
    <circle cx="11" cy="12" r="1" fill="currentColor" />
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// Custom fee confirmation modal
const ConfirmModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
`;

const ConfirmModalContent = styled.div`
  background: ${({ theme }) =>
    theme?.name === 'dark' ? '#1a1a2e' : '#ffffff'};
  border-radius: 20px;
  padding: 32px 40px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.4);
  text-align: center;
`;

const ConfirmModalIcon = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(245, 158, 11, 0.15)'
      : 'rgba(245, 158, 11, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #f59e0b;
`;

const ConfirmModalTitle = styled.h3`
  font-size: 22px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#ffffff'};
  margin: 0 0 12px 0;
`;

const ConfirmModalText = styled.p`
  font-size: 15px;
  color: ${({ theme }) => theme?.colors?.textGray?.secondary || '#94A3B8'};
  margin: 0 0 28px 0;
  line-height: 1.6;
`;

const ConfirmModalHighlight = styled.span`
  color: ${({ theme }) => theme?.colors?.text?.primary || '#ffffff'};
  font-weight: 500;
`;

const ConfirmModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const ConfirmModalButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 120px;

  ${({ variant, theme }) =>
    variant === 'primary'
      ? `
    background: #fa4616;
    color: white;
    border: none;
    &:hover {
      background: #e03d10;
    }
  `
      : `
    background: transparent;
    color: ${theme?.colors?.text?.primary || '#ffffff'};
    border: 1px solid ${
      theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
    };
    &:hover {
      border-color: ${
        theme?.name === 'dark'
          ? 'rgba(255, 255, 255, 0.4)'
          : 'rgba(0, 0, 0, 0.4)'
      };
    }
  `}
`;

const formatFee = (dollar, percent) =>
  `$${(dollar || 0).toFixed(2)} + ${percent || 0}%`;

// Platform detection
const isTicketHoss = process.env.REACT_APP_PLATFORM === 'tickethoss';

const TICKET_TYPES = [
  {
    value: 'ticket',
    label: isTicketHoss ? 'Ticket' : 'Pit Pass - (Serves Waiver)',
  },
  {
    value: 'other',
    label: isTicketHoss ? 'Other' : 'Other - (Default % - No Waiver)',
  },
  ...(isTicketHoss ? [{ value: 'bundle', label: 'Bundle' }] : []),
  ...(!isTicketHoss
    ? [
        {
          value: 'registration',
          label: 'Registration (Form + Pages and or Waiver)',
        },
      ]
    : []),
];

const VISIBILITY_OPTIONS = [
  { value: 'visible', label: 'App & POS' },
  { value: 'app_only', label: 'App Only' },
  { value: 'pos_only', label: 'POS Only' },
  { value: 'hidden', label: 'Hidden' },
];

const EditEventTicketContent = () => {
  const { eventId, ticketId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const inputRef = useRef(null);
  const theme = useTheme();
  const infoIconColor =
    theme?.colors?.textGray?.dark ||
    (theme?.name === 'dark' ? '#e2e8f0' : '#64748b');

  // Context-aware routing
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  // Check if we came from the wizard (via location state)
  const cameFromWizard = location.state?.fromWizard === true;

  // Normalize a date string to MM-DD-YYYY format for the backend
  const normalizeDate = (dateStr) => {
    if (!dateStr) return null;
    // Already in MM-DD-YYYY format
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) return dateStr;
    // Convert from YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [y, m, d] = dateStr.split('-');
      return `${m}-${d}-${y}`;
    }
    return dateStr;
  };

  // Mutation for updating event tickets
  const { updateEventTicket } = useUpdateEventTicket();

  // Fetch event data with all ticket arrays
  const {
    data: eventData,
    loading: eventLoading,
    error: eventError,
  } = useQuery(GET_EVENT_WITH_TICKETS, {
    variables: { input: { id: parseInt(eventId) } },
    skip: !eventId,
  });
  const event = eventData?.getEvent;

  // Fetch seating charts for reserved seating
  const { data: chartsData, loading: chartsLoading } =
    useQuery(GET_SEATING_CHARTS);
  const seatingChartOptions = (chartsData?.getSeatingCharts || []).map(
    (chart) => ({
      value: chart.id,
      label: chart.name,
    })
  );

  // Fetch waivers for pages
  const { data: waiversData } = useQuery(GET_WAIVERS);
  const waivers = sortWaivers(waiversData?.getWaivers || []);

  // Fetch Formstack forms for pages
  // Initial empty query loads default forms, then search refines results
  const [formstackSearch, setFormstackSearch] = useState('');
  const { data: formstackData, loading: formstackLoading } = useQuery(
    SEARCH_FORMSTACK_FORMS,
    {
      variables: { queryString: formstackSearch },
    }
  );
  const baseFormstackOptions = (formstackData?.searchFormstackForms || []).map(
    (form) => ({
      value: form.form_id,
      label: form.name,
    })
  );

  // Track initial form_ids from ticket pages (for display before searching)
  const [initialFormIds, setInitialFormIds] = useState([]);

  // Combine search results with initial form_ids for display
  // If existing form_id is found in search results, use its name; otherwise show "Form #id"
  const formstackOptions = useMemo(() => {
    const options = [...baseFormstackOptions];
    initialFormIds.forEach((formId) => {
      if (!options.some((o) => String(o.value) === String(formId))) {
        options.push({
          value: formId,
          label: `Form #${formId}`,
        });
      }
    });
    return options;
  }, [baseFormstackOptions, initialFormIds]);

  const handleFormstackSearch = (query) => {
    setFormstackSearch(query);
  };

  // Find the ticket from event data
  const { ticket, ticketType } = useMemo(() => {
    if (!ticketId || !event) return { ticket: null, ticketType: null };

    const id = parseInt(ticketId);

    // Check admin_tickets (single-day)
    const singleDay = event.admin_tickets?.find((t) => parseInt(t.id) === id);
    if (singleDay) return { ticket: singleDay, ticketType: 'ticket' };

    // Check admin_multiday_tickets (still type 'ticket', multiday just means it has an end_date)
    const multiDay = event.admin_multiday_tickets?.find(
      (t) => parseInt(t.id) === id
    );
    if (multiDay) return { ticket: multiDay, ticketType: 'ticket' };

    // Check admin_other_tickets
    const other = event.admin_other_tickets?.find((t) => parseInt(t.id) === id);
    if (other) return { ticket: other, ticketType: 'other' };

    // Check admin_bundle_tickets
    const bundle = event.admin_bundle_tickets?.find(
      (t) => parseInt(t.id) === id
    );
    if (bundle) return { ticket: bundle, ticketType: 'bundle' };

    // Check admin_registration_tickets
    const registration = event.admin_registration_tickets?.find(
      (t) => parseInt(t.id) === id
    );
    if (registration)
      return { ticket: registration, ticketType: 'registration' };

    return { ticket: null, ticketType: null };
  }, [event, ticketId]);

  // Fetch categories for all ticket types (including registration)
  const { data: categoriesData } = useGetAllCategories();
  const categories = categoriesData?.getAllCategories || [];

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: 'ticket',
    description: '',
    description_image_id: null,
    description_image: null,
    description_video: '',
    start_date: '',
    end_date: '',
    sale_start: '',
    sale_end: '',
    on_sale_time: '',
    off_sale_time: '23:59',
    color_code: '',
    price: '',
    track_price: '',
    fee_dollar: '',
    fee_percent: '',
    pos_fee_dollar: '',
    pos_fee_percent: '',
    visibility: 'visible',
    limit: '',
    is_featured: false,
    featured_text: '',
    category_id: '',
    reserved_seat: false,
    reserved_seat_category: '',
    reserved_seat_prefix: '',
    reserved_seat_section: false,
    seating_chart_id: null,
    // Member number settings (registration only)
    set_member_number: false,
    prefix_text: '',
    suffix_text: '',
    last_member_number: '',
    // Bundle-specific
    associated_tickets: [],
    // Registration pages
    pages: [],
    pages_with_order: [],
    // Sales windows toggle
    has_sales_windows: false,
    // Ticket-level tiers
    tiers: [],
    // Sales windows
    windows: [],
  });
  const [errors, setErrors] = useState({});
  const [toaster, setToaster] = useState({ isOpen: false });
  const [saving, setSaving] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [pendingFeeReset, setPendingFeeReset] = useState(null);

  // Check ticket types for conditional rendering
  const isBundle = ticketType === 'bundle';
  const isRegistration = ticketType === 'registration';

  const categoryOptions = useMemo(() => {
    // Bundles use the same categories as regular tickets
    const effectiveType = formData.type === 'bundle' ? 'ticket' : formData.type;
    const visible = effectiveType
      ? categories.filter((cat) => cat.type === effectiveType)
      : [];
    return visible.map((cat) => ({
      value: cat.id,
      label: cat.name,
    }));
  }, [categories, formData.type]);

  // Get available tickets for bundle (all non-bundle tickets)
  const availableTicketsForBundle = useMemo(() => {
    if (!event) return [];
    return [
      ...(event.admin_tickets || []),
      ...(event.admin_multiday_tickets || []),
      ...(event.admin_other_tickets || []),
      ...(event.admin_registration_tickets || []),
    ];
  }, [event]);

  // Pre-fill form with ticket data when available
  useEffect(() => {
    if (ticket && !isInitialized) {
      // Process pages data if present (admin_pages for registration tickets, pages for others)
      const ticketPages = ticket.admin_pages || ticket.pages || [];
      const processedPages = ticketPages
        .map((page) => ({
          ...page,
          id: page.id || `page_${Date.now()}_${Math.random()}`,
          waivers: page.waivers
            ? (Array.isArray(page.waivers) ? page.waivers : [page.waivers])
                .filter(Boolean)
                .map((waiver) => ({ waiver: waiver }))
            : [],
        }))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      // Track initial form_ids for display in dropdowns before searching
      const formIdsFromPages = processedPages
        .filter((p) => p.form_id)
        .map((p) => parseInt(p.form_id));
      setInitialFormIds(formIdsFromPages);

      // Process tiers (shown most expensive first, matching the app)
      const processedTiers = sortTiersByPriceDesc(ticket.tiers).map((tier) => ({
        id: tier.id || `tier_${Date.now()}_${Math.random()}`,
        name: tier.name || '',
        price: tier.price?.toString() || '',
        track_price: tier.track_price?.toString() || '',
        color_code: tier.color_code || '',
      }));

      // Process windows with their tiers
      const processedWindows = (ticket.windows || []).map((window) => ({
        id: window.id || `window_${Date.now()}_${Math.random()}`,
        name: window.name || '',
        ends: window.ends || '1 week before event',
        time: window.time ? window.time.substring(0, 5) : '23:59',
        price: window.price?.toString() || '',
        tiers: sortTiersByPriceDesc(window.tiers).map((tier) => ({
          id: tier.id || `tier_${Date.now()}_${Math.random()}`,
          name: tier.name || '',
          price: tier.price?.toString() || '',
          track_price: tier.track_price?.toString() || '',
          color_code: tier.color_code || '',
        })),
      }));

      setFormData({
        name: ticket.name || '',
        type: ticketType || 'ticket',
        description: ticket.description || '',
        description_image_id: ticket.description_image_id || null,
        description_image: ticket.description_image || null,
        description_video: ticket.description_video || '',
        start_date: ticket.start_date || '',
        end_date: ticket.end_date || '',
        sale_start: ticket.sale_start || '',
        sale_end: ticket.sale_end || '',
        on_sale_time: ticket.on_sale_time || '',
        off_sale_time: ticket.off_sale_time || '23:59',
        color_code: ticket.color_code || '',
        price: ticket.price?.toString() || '',
        track_price: ticket.track_price?.toString() || '',
        fee_dollar: ticket.fee_dollar?.toString() || '',
        fee_percent: ticket.fee_percent?.toString() || '',
        pos_fee_dollar: ticket.pos_fee_dollar?.toString() || '',
        pos_fee_percent: ticket.pos_fee_percent?.toString() || '',
        visibility: ticket.visibility || 'visible',
        limit: ticket.limit?.toString() || '',
        is_featured: ticket.is_featured || false,
        featured_text: ticket.featured_text || '',
        category_id: ticket.category_id?.toString() || '',
        reserved_seat: ticket.reserved_seat || false,
        reserved_seat_category: ticket.reserved_seat_category || '',
        reserved_seat_prefix: ticket.reserved_seat_prefix || '',
        reserved_seat_section: ticket.reserved_seat_section || false,
        seating_chart_id: ticket.chart_id || null,
        // Member number settings
        set_member_number: ticket.set_member_number || false,
        prefix_text: ticket.prefix_text || '',
        suffix_text: ticket.suffix_text || '',
        last_member_number: ticket.last_member_number?.toString() || '',
        // Bundle-specific: pre-fill associated tickets
        associated_tickets: ticket.associated_tickets || [],
        // Registration pages
        pages: processedPages,
        pages_with_order: processedPages,
        // Sales windows and tiers
        has_sales_windows: (ticket.windows?.length || 0) > 0,
        tiers: processedTiers,
        windows: processedWindows,
      });
      setIsInitialized(true);
    }
  }, [ticket, ticketType, isInitialized]);

  const applyFieldChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      // Re-apply default fees when type, price, or reserved seating changes
      // (mirrors the create ticket form). User-interaction driven, so it never
      // overrides fees loaded for an existing ticket on mount.
      if (field === 'type' || field === 'price' || field === 'reserved_seat') {
        const numPrice = parseFloat(updated.price) || 0;
        if (numPrice >= 1) {
          const { fee_dollar, fee_percent } = getDefaultTicketFees(
            updated.price,
            updated.type,
            updated.reserved_seat
          );
          const { pos_fee_dollar, pos_fee_percent } = getDefaultPosFees(
            updated.price,
            updated.reserved_seat
          );
          if (fee_dollar !== undefined)
            updated.fee_dollar = fee_dollar.toString();
          if (fee_percent !== undefined)
            updated.fee_percent = fee_percent.toString();
          if (pos_fee_dollar !== undefined)
            updated.pos_fee_dollar = pos_fee_dollar.toString();
          if (pos_fee_percent !== undefined)
            updated.pos_fee_percent = pos_fee_percent.toString();
        }
      }

      return updated;
    });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleChange = (field, value) => {
    // Changing the type or reserved seating resets fees to the new defaults.
    // If the current fees don't match the defaults for the ticket's current
    // type/price/seating, they were customized - confirm before overwriting.
    if (
      (field === 'type' || field === 'reserved_seat') &&
      value !== formData[field]
    ) {
      const numPrice = parseFloat(formData.price) || 0;
      if (numPrice >= 1) {
        const oldFees = getDefaultTicketFees(
          formData.price,
          formData.type,
          formData.reserved_seat
        );
        const oldPosFees = getDefaultPosFees(
          formData.price,
          formData.reserved_seat
        );
        const differs = (current, def) =>
          def !== undefined && (parseFloat(current) || 0) !== def;
        const hasCustomFees =
          differs(formData.fee_dollar, oldFees.fee_dollar) ||
          differs(formData.fee_percent, oldFees.fee_percent) ||
          differs(formData.pos_fee_dollar, oldPosFees.pos_fee_dollar) ||
          differs(formData.pos_fee_percent, oldPosFees.pos_fee_percent);

        if (hasCustomFees) {
          setPendingFeeReset({ field, value });
          return;
        }
      }
    }
    applyFieldChange(field, value);
  };

  const handleKeepCustomFees = () => {
    const { field, value } = pendingFeeReset;
    setPendingFeeReset(null);
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleUseDefaultFees = () => {
    const { field, value } = pendingFeeReset;
    setPendingFeeReset(null);
    applyFieldChange(field, value);
  };

  // Attachment handlers for description image
  const handleAttachmentsChange = (attachments) => {
    if (attachments.length > 0) {
      setFormData((prev) => ({
        ...prev,
        description_image_id: parseInt(attachments[0].id) || null,
        description_image: attachments[0].url,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        description_image_id: null,
        description_image: null,
      }));
    }
  };

  const getAttachmentsFromImage = () => {
    if (!formData.description_image) return [];
    return [
      {
        id: formData.description_image_id || 'description_image',
        name: 'Description Image',
        size: 0,
        sizeFormatted: '',
        url: formData.description_image,
        type: 'image/*',
        isImage: true,
      },
    ];
  };

  // Tier handlers
  const addTier = () => {
    const newTier = {
      id: `tier_${Date.now()}`,
      name: '',
      price: '',
      track_price: '',
      color_code: '',
    };
    setFormData((prev) => ({
      ...prev,
      tiers: [...prev.tiers, newTier],
    }));
  };

  const removeTier = (index) => {
    setFormData((prev) => ({
      ...prev,
      tiers: prev.tiers.filter((_, i) => i !== index),
    }));
  };

  const updateTier = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      tiers: prev.tiers.map((tier, i) =>
        i === index ? { ...tier, [field]: value } : tier
      ),
    }));
  };

  // Sales Windows handlers
  const addSalesWindow = () => {
    const newWindow = {
      id: `window_${Date.now()}`,
      name: '',
      ends: '',
      time: '23:59',
      price: '',
      tiers: [],
    };
    setFormData((prev) => ({
      ...prev,
      windows: [...prev.windows, newWindow],
    }));
  };

  const removeSalesWindow = (index) => {
    setFormData((prev) => ({
      ...prev,
      windows: prev.windows.filter((_, i) => i !== index),
    }));
  };

  const updateSalesWindow = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      windows: prev.windows.map((window, i) =>
        i === index ? { ...window, [field]: value } : window
      ),
    }));
  };

  // Tier handlers for sales windows
  const addTierToWindow = (windowIndex) => {
    const newTier = {
      id: `tier_${Date.now()}`,
      name: '',
      price: '',
      track_price: '',
      color_code: '',
    };
    setFormData((prev) => ({
      ...prev,
      windows: prev.windows.map((window, i) =>
        i === windowIndex
          ? { ...window, tiers: [...window.tiers, newTier] }
          : window
      ),
    }));
  };

  const removeTierFromWindow = (windowIndex, tierIndex) => {
    setFormData((prev) => ({
      ...prev,
      windows: prev.windows.map((window, i) =>
        i === windowIndex
          ? {
              ...window,
              tiers: window.tiers.filter((_, ti) => ti !== tierIndex),
            }
          : window
      ),
    }));
  };

  const updateTierInWindow = (windowIndex, tierIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      windows: prev.windows.map((window, i) =>
        i === windowIndex
          ? {
              ...window,
              tiers: window.tiers.map((tier, ti) =>
                ti === tierIndex ? { ...tier, [field]: value } : tier
              ),
            }
          : window
      ),
    }));
  };

  // Registration Pages handlers
  const addPage = () => {
    const newOrder =
      formData.pages.length > 0
        ? getHighestItemOrderNumber(formData.pages) + 1
        : 1;
    const newPage = {
      id: `page_${Date.now()}`,
      name: '',
      pdf_url: '',
      video_url: '',
      pdf_id: null,
      form_id: null,
      waivers: [],
      order: newOrder,
    };
    setFormData((prev) => ({
      ...prev,
      pages: [...prev.pages, newPage],
      pages_with_order: [...prev.pages_with_order, newPage],
    }));
  };

  const removePage = (index) => {
    const pageToRemove = formData.pages[index];
    setFormData((prev) => ({
      ...prev,
      pages: prev.pages.filter((_, i) => i !== index),
      pages_with_order: prev.pages_with_order.filter((p) =>
        pageToRemove.id
          ? p.id !== pageToRemove.id
          : p.name !== pageToRemove.name
      ),
    }));
  };

  const updatePage = (index, field, value) => {
    setFormData((prev) => {
      const updatedPages = prev.pages.map((page, i) =>
        i === index ? { ...page, [field]: value } : page
      );
      const pageToUpdate = prev.pages[index];
      const updatedPagesWithOrder = prev.pages_with_order.map((p) =>
        (pageToUpdate.id && p.id === pageToUpdate.id) ||
        (!pageToUpdate.id && p.order === pageToUpdate.order)
          ? { ...p, [field]: value }
          : p
      );
      return {
        ...prev,
        pages: updatedPages,
        pages_with_order: updatedPagesWithOrder,
      };
    });
  };

  const addWaiverToPage = (pageIndex) => {
    setFormData((prev) => {
      const pageToUpdate = prev.pages[pageIndex];
      const updatedPages = prev.pages.map((page, i) =>
        i === pageIndex
          ? { ...page, waivers: [...(page.waivers || []), { waiver: '' }] }
          : page
      );
      const updatedPagesWithOrder = prev.pages_with_order.map((p) =>
        (pageToUpdate.id && p.id === pageToUpdate.id) ||
        (!pageToUpdate.id && p.order === pageToUpdate.order)
          ? { ...p, waivers: [...(p.waivers || []), { waiver: '' }] }
          : p
      );
      return {
        ...prev,
        pages: updatedPages,
        pages_with_order: updatedPagesWithOrder,
      };
    });
  };

  const removeWaiverFromPage = (pageIndex, waiverIndex) => {
    setFormData((prev) => {
      const pageToUpdate = prev.pages[pageIndex];
      const updatedPages = prev.pages.map((page, i) =>
        i === pageIndex
          ? {
              ...page,
              waivers: page.waivers.filter((_, wi) => wi !== waiverIndex),
            }
          : page
      );
      const updatedPagesWithOrder = prev.pages_with_order.map((p) =>
        (pageToUpdate.id && p.id === pageToUpdate.id) ||
        (!pageToUpdate.id && p.order === pageToUpdate.order)
          ? {
              ...p,
              waivers: p.waivers.filter((_, wi) => wi !== waiverIndex),
            }
          : p
      );
      return {
        ...prev,
        pages: updatedPages,
        pages_with_order: updatedPagesWithOrder,
      };
    });
  };

  const updateWaiverInPage = (pageIndex, waiverIndex, value) => {
    setFormData((prev) => {
      const pageToUpdate = prev.pages[pageIndex];
      const updatedPages = prev.pages.map((page, i) =>
        i === pageIndex
          ? {
              ...page,
              waivers: page.waivers.map((w, wi) =>
                wi === waiverIndex ? { waiver: value } : w
              ),
            }
          : page
      );
      const updatedPagesWithOrder = prev.pages_with_order.map((p) =>
        (pageToUpdate.id && p.id === pageToUpdate.id) ||
        (!pageToUpdate.id && p.order === pageToUpdate.order)
          ? {
              ...p,
              waivers: p.waivers.map((w, wi) =>
                wi === waiverIndex ? { waiver: value } : w
              ),
            }
          : p
      );
      return {
        ...prev,
        pages: updatedPages,
        pages_with_order: updatedPagesWithOrder,
      };
    });
  };

  const handlePagesSort = (sortedPages) => {
    const reorderedPages = sortedPages.map((page, index) => ({
      ...page,
      order: index + 1,
    }));
    setFormData((prev) => ({
      ...prev,
      pages_with_order: reorderedPages,
      pages: prev.pages.map((page) => {
        const sortedPage = reorderedPages.find((p) => p.id === page.id);
        return sortedPage ? { ...page, order: sortedPage.order } : page;
      }),
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Ticket name is required';
    }

    if (!formData.category_id) {
      newErrors.category_id = 'Category is required';
    }

    // Bundles don't require dates
    if (!isBundle && !formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }

    if (!formData.price && formData.price !== 0) {
      newErrors.price = 'Price is required';
    } else if (
      isNaN(parseFloat(formData.price)) ||
      parseFloat(formData.price) < 0
    ) {
      newErrors.price = 'Price must be a valid positive number';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Validation Error',
        message: Object.values(newErrors)[0],
      });
      return false;
    }

    return true;
  };

  const handleBack = () => {
    if (cameFromWizard) {
      // Go back to the ticket wizard (create event flow)
      history.push(`${routePrefix}/events/addEvent/${eventId}/tickets`);
    } else {
      // Go back to the edit event page
      history.push(`${routePrefix}/events/editEvent/${eventId}?tab=tickets`);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);

    try {
      // Build the mutation input
      const input = {
        id: Number(ticketId),
        event_id: Number(eventId),
        name: formData.name.trim(),
        type: formData.type,
        description: formData.description || null,
        description_image_id: formData.description_image_id || null,
        description_video: formData.description_video || null,
        start_date: normalizeDate(formData.start_date),
        end_date: normalizeDate(formData.end_date || formData.start_date),
        sale_start: normalizeDate(formData.sale_start) || null,
        sale_end: normalizeDate(formData.sale_end) || null,
        on_sale_time: formData.on_sale_time || null,
        off_sale_time: formData.off_sale_time || null,
        color_code: formData.color_code || null,
        price: parseFloat(formData.price) || 0,
        track_price: toNullablePrice(formData.track_price),
        fee_dollar: parseFloat(formData.fee_dollar) || 0,
        fee_percent: parseFloat(formData.fee_percent) || 0,
        pos_fee_dollar: parseFloat(formData.pos_fee_dollar) || 0,
        pos_fee_percent: parseFloat(formData.pos_fee_percent) || 0,
        visibility: formData.visibility || 'visible',
        limit: formData.limit ? parseInt(formData.limit) : null,
        is_featured: formData.is_featured || false,
        featured_text: formData.featured_text || '',
        category_id: formData.category_id ? Number(formData.category_id) : null,
        reserved_seat: formData.reserved_seat || false,
        reserved_seat_category: formData.reserved_seat_category || '',
        reserved_seat_prefix: formData.reserved_seat_prefix || '',
        reserved_seat_section: formData.reserved_seat_section || false,
        chart_id: formData.seating_chart_id || null,
        // Member number settings (registration only)
        set_member_number: formData.set_member_number || false,
        prefix_text: formData.prefix_text || null,
        suffix_text: formData.suffix_text || null,
        last_member_number: formData.last_member_number
          ? parseInt(formData.last_member_number)
          : null,
        // Bundle-specific: associated tickets
        associated_tickets:
          formData.associated_tickets?.length > 0
            ? formData.associated_tickets.map((t) => ({
                ticket_id: Number(t.id),
                quantity: t.quantity || 1,
              }))
            : null,
        // Registration-specific: pages
        pages:
          formData.pages_with_order?.length > 0
            ? formData.pages_with_order.map((p) => ({
                id:
                  p.id && !String(p.id).startsWith('page_')
                    ? Number(p.id)
                    : null,
                name: p.name,
                pdf_url: p.pdf_url,
                video_url: p.video_url,
                pdf_id: p.pdf_id ? Number(p.pdf_id) : null,
                form_id: p.form_id ? String(p.form_id) : null,
                waivers: (p.waivers || [])
                  .map((w) => w.waiver)
                  .filter(Boolean)
                  .map((w) => ({ waiver_id: Number(w) })),
                order: p.order,
              }))
            : null,
        // Include windows if present. Send real numeric ids for existing
        // windows/tiers (null for new ones) so the backend updates them in place
        // instead of wiping and recreating - which would null purchases' type_id.
        windows:
          formData.windows?.length > 0
            ? formData.windows.map((w) => ({
                id:
                  w.id && !String(w.id).startsWith('window_')
                    ? Number(w.id)
                    : null,
                name: w.name,
                ends: w.ends,
                time: w.time,
                price: parseFloat(w.price) || 0,
                tiers: w.tiers?.map((t) => ({
                  id:
                    t.id && !String(t.id).startsWith('tier_')
                      ? Number(t.id)
                      : null,
                  name: t.name,
                  price: parseFloat(t.price) || 0,
                  track_price: toNullablePrice(t.track_price),
                  color_code: t.color_code || null,
                })),
              }))
            : null,
        // Include tiers if present
        tiers:
          formData.tiers?.length > 0
            ? formData.tiers.map((t) => ({
                id:
                  t.id && !String(t.id).startsWith('tier_')
                    ? Number(t.id)
                    : null,
                name: t.name,
                price: parseFloat(t.price) || 0,
                track_price: toNullablePrice(t.track_price),
                color_code: t.color_code || null,
              }))
            : null,
      };

      // Call the mutation
      const response = await updateEventTicket(input);

      if (response?.data?.updateEventTicket?.id) {
        setToaster({
          isOpen: true,
          type: 'success',
          title: 'Ticket Updated',
          message: `"${formData.name.trim()}" has been updated.`,
        });

        // Navigate back after showing success message
        setTimeout(() => {
          handleBack();
        }, 1500);
      }
    } catch (error) {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to update ticket. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  if (eventLoading) {
    return (
      <PageWrapper>
        <Container>
          <LoadingContainer>Loading ticket...</LoadingContainer>
        </Container>
      </PageWrapper>
    );
  }

  if (eventError) {
    return (
      <PageWrapper>
        <Container>
          <BackButtonWrapper>
            <ButtonV2
              type="button"
              onClick={handleBack}
              icon={<ArrowLeftIcon />}
            >
              Back to Event
            </ButtonV2>
          </BackButtonWrapper>
          <NotFoundContainer>
            <span style={{ color: '#ef4444' }}>
              Failed to load ticket data. Please try again.
            </span>
            <ButtonV2 type="button" onClick={handleBack}>
              Return to Tickets
            </ButtonV2>
          </NotFoundContainer>
        </Container>
      </PageWrapper>
    );
  }

  if (!ticket) {
    return (
      <PageWrapper>
        <Container>
          <BackButtonWrapper>
            <ButtonV2
              type="button"
              onClick={handleBack}
              icon={<ArrowLeftIcon />}
            >
              Back to Event
            </ButtonV2>
          </BackButtonWrapper>
          <NotFoundContainer>
            <span>Ticket not found</span>
            <ButtonV2 type="button" onClick={handleBack}>
              Return to Tickets
            </ButtonV2>
          </NotFoundContainer>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Container>
        <BackButtonWrapper>
          <ButtonV2 type="button" onClick={handleBack} icon={<ArrowLeftIcon />}>
            Back to Event
          </ButtonV2>
        </BackButtonWrapper>

        <PageHeader>
          <HeaderLeft>
            <Title>Edit Ticket</Title>
            <Subtitle>
              Editing "{ticket.name}" for {event?.name}
            </Subtitle>
          </HeaderLeft>
          <HeaderRight>
            <HeaderButtonWrapper>
              <ButtonV2 type="button" onClick={handleBack}>
                Cancel
              </ButtonV2>
            </HeaderButtonWrapper>
            <HeaderButtonWrapper>
              <ButtonV2
                elevated
                type="button"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </ButtonV2>
            </HeaderButtonWrapper>
          </HeaderRight>
        </PageHeader>

        {/* Basic Information */}
        <FormCard>
          <FormSection>
            <SectionTitle>Basic Information</SectionTitle>

            <FormRow columns="2fr 1fr">
              <FormGroup>
                <Label>
                  Ticket Name
                  <RequiredBadge>*Required</RequiredBadge>
                </Label>
                <Input
                  ref={inputRef}
                  type="text"
                  placeholder="e.g., General Admission - Saturday"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  error={!!errors.name}
                />
                {errors.name && <ErrorText>{errors.name}</ErrorText>}
              </FormGroup>

              <FormFieldSelect
                label="Type"
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
                options={TICKET_TYPES}
              />
            </FormRow>

            <FormGroup>
              <Label>Description</Label>
              <Textarea
                placeholder="Enter ticket description..."
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
              />
            </FormGroup>

            <FormRow columns="1fr 1fr" style={{ marginTop: '16px' }}>
              <FormGroup>
                <Label>Description Image</Label>
                <AttachmentUpload
                  attachments={getAttachmentsFromImage()}
                  onAttachmentsChange={handleAttachmentsChange}
                  maxFiles={1}
                  maxFileSize={5242880}
                  channel="SMS"
                />
              </FormGroup>

              <FormGroup>
                <Label>Description URL (Video)</Label>
                <Input
                  type="url"
                  placeholder="e.g., https://vimeo.com/... or https://youtube.com/..."
                  value={formData.description_video}
                  onChange={(e) =>
                    handleChange('description_video', e.target.value)
                  }
                />
              </FormGroup>
            </FormRow>
          </FormSection>
        </FormCard>

        {/* Date & Color (not for bundles) */}
        {!isBundle && (
          <FormCard>
            <FormSection>
              <SectionTitle>Date & Color</SectionTitle>

              <FormRow columns="1fr 1fr 1fr">
                <FormGroup>
                  <Label>
                    Start Date
                    <RequiredBadge>*Required</RequiredBadge>
                  </Label>
                  <DateTimePicker
                    value={formData.start_date}
                    onChange={(value) => handleChange('start_date', value)}
                    showTime={false}
                  />
                  {errors.start_date && (
                    <ErrorText>{errors.start_date}</ErrorText>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>End Date</Label>
                  <DateTimePicker
                    value={formData.end_date}
                    onChange={(value) => handleChange('end_date', value)}
                    showTime={false}
                  />
                </FormGroup>

                <ColorSelect
                  label="Ticket Color"
                  value={formData.color_code}
                  onChange={(value) => handleChange('color_code', value)}
                  options={COLOR_CODES}
                  placeholder="Select Color"
                  allowEmpty
                  emptyLabel="Select Color"
                />
              </FormRow>

              {/* Sale Dates & Times */}
              <SectionTitle style={{ marginTop: '24px' }}>
                Sale Dates & Times
              </SectionTitle>
              <FormRow columns="1fr 1fr 1fr 1fr">
                <FormGroup>
                  <Label>Sale Start Date</Label>
                  <DateTimePicker
                    value={formData.sale_start}
                    onChange={(value) => handleChange('sale_start', value)}
                    showTime={false}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>On Sale Time</Label>
                  <DateTimePicker
                    value={formData.on_sale_time}
                    onChange={(value) => handleChange('on_sale_time', value)}
                    timeOnly
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Sale End Date</Label>
                  <DateTimePicker
                    value={formData.sale_end}
                    onChange={(value) => handleChange('sale_end', value)}
                    showTime={false}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Off Sale Time</Label>
                  <DateTimePicker
                    value={formData.off_sale_time}
                    onChange={(value) => handleChange('off_sale_time', value)}
                    timeOnly
                  />
                </FormGroup>
              </FormRow>

              {/* Feature Toggles - only show if event has these features enabled */}
              {event?.reserved_seats && (
                <TwoColumnGrid style={{ marginTop: '24px' }}>
                  <ToggleCard>
                    <ToggleInfo>
                      <ToggleTitle>Reserved Seating</ToggleTitle>
                      <ToggleDescription>
                        Enable seat selection for this ticket
                      </ToggleDescription>
                    </ToggleInfo>
                    <Toggle
                      checked={formData.reserved_seat}
                      onChange={(e) =>
                        handleChange('reserved_seat', e.target.checked)
                      }
                    />
                  </ToggleCard>
                </TwoColumnGrid>
              )}
            </FormSection>
          </FormCard>
        )}

        {/* Bundle-specific: Color, sale dates, and ticket selection */}
        {isBundle && (
          <FormCard>
            <FormSection>
              <SectionTitle>Bundle Color</SectionTitle>
              <FormRow columns="1fr 2fr">
                <ColorSelect
                  label="Color"
                  value={formData.color_code}
                  onChange={(value) => handleChange('color_code', value)}
                  options={COLOR_CODES}
                  placeholder="Select Color"
                  allowEmpty
                  emptyLabel="Select Color"
                />
                <div />
              </FormRow>

              {/* Sale Dates & Times */}
              <SectionTitle style={{ marginTop: '24px' }}>
                Sale Dates & Times
              </SectionTitle>
              <FormRow columns="1fr 1fr 1fr 1fr">
                <FormGroup>
                  <Label>Sale Start Date</Label>
                  <DateTimePicker
                    value={formData.sale_start}
                    onChange={(value) => handleChange('sale_start', value)}
                    showTime={false}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>On Sale Time</Label>
                  <DateTimePicker
                    value={formData.on_sale_time}
                    onChange={(value) => handleChange('on_sale_time', value)}
                    timeOnly
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Sale End Date</Label>
                  <DateTimePicker
                    value={formData.sale_end}
                    onChange={(value) => handleChange('sale_end', value)}
                    showTime={false}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Off Sale Time</Label>
                  <DateTimePicker
                    value={formData.off_sale_time}
                    onChange={(value) => handleChange('off_sale_time', value)}
                    timeOnly
                  />
                </FormGroup>
              </FormRow>

              {/* Bundle Tickets Selection */}
              <BundleTicketsSection
                availableTickets={availableTicketsForBundle}
                selectedTickets={formData.associated_tickets}
                onChange={(newAssociatedTickets) =>
                  handleChange('associated_tickets', newAssociatedTickets)
                }
              />
            </FormSection>
          </FormCard>
        )}

        {/* Pricing */}
        <FormCard>
          <FormSection>
            <SectionTitle>Pricing</SectionTitle>

            <FormRow columns="1fr 1fr">
              <FormGroup>
                <Label>
                  Price
                  <RequiredBadge>*Required</RequiredBadge>
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.price}
                  onChange={(e) => handleChange('price', e.target.value)}
                  error={!!errors.price}
                />
                {errors.price && <ErrorText>{errors.price}</ErrorText>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="ticket-track-price">At Track Price</Label>
                <Input
                  id="ticket-track-price"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.track_price}
                  onChange={(e) => handleChange('track_price', e.target.value)}
                />
              </FormGroup>
            </FormRow>

            <SectionTitle style={{ marginTop: '24px' }}>Fees</SectionTitle>
            <FormRow columns="1fr 1fr 1fr 1fr">
              <FormGroup>
                <Label>Fee Dollar Amount (App)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.fee_dollar}
                  onChange={(e) => handleChange('fee_dollar', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <LabelWithTooltip>
                  Fee Percent (App)
                  <Tooltip
                    info
                    content="This percentage needs to include the 3.25% base processing fee."
                    iconColor={infoIconColor}
                    iconSize="16px"
                  />
                </LabelWithTooltip>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.fee_percent}
                  onChange={(e) => handleChange('fee_percent', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <Label>Fee Dollar Amount (POS)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.pos_fee_dollar}
                  onChange={(e) =>
                    handleChange('pos_fee_dollar', e.target.value)
                  }
                />
              </FormGroup>

              <FormGroup>
                <LabelWithTooltip>
                  Fee Percent (POS)
                  <Tooltip
                    info
                    content="This percentage needs to include the 3.25% base processing fee."
                    iconColor={infoIconColor}
                    iconSize="16px"
                  />
                </LabelWithTooltip>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.pos_fee_percent}
                  onChange={(e) =>
                    handleChange('pos_fee_percent', e.target.value)
                  }
                />
              </FormGroup>
            </FormRow>

            {/* Tiered Pricing - Only for reserved seats WITHOUT sales windows */}
            {formData.reserved_seat && !formData.has_sales_windows && (
              <TierSection>
                <TierHeader>
                  <TierTitle>Tiered Pricing</TierTitle>
                  <AddTierButtonWrapper>
                    <ButtonV2
                      elevated
                      type="button"
                      onClick={addTier}
                      icon={<PlusIcon />}
                    >
                      Add
                    </ButtonV2>
                  </AddTierButtonWrapper>
                </TierHeader>

                {formData.tiers.length === 0 ? (
                  <EmptyTiersMessage>
                    No pricing tiers configured. Add tiers for different seat
                    categories.
                  </EmptyTiersMessage>
                ) : (
                  <>
                    <TierRow>
                      <Label>Label (Name)</Label>
                      <Label>App Price</Label>
                      <Label>Track Price</Label>
                      <Label>Color</Label>
                      <div />
                    </TierRow>
                    {formData.tiers.map((tier, index) => (
                      <TierRow key={tier.id}>
                        <Input
                          type="text"
                          placeholder="e.g., Front Row"
                          value={tier.name}
                          onChange={(e) =>
                            updateTier(index, 'name', e.target.value)
                          }
                        />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={tier.price}
                          onChange={(e) =>
                            updateTier(index, 'price', e.target.value)
                          }
                        />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={tier.track_price}
                          onChange={(e) =>
                            updateTier(index, 'track_price', e.target.value)
                          }
                        />
                        <ColorSelect
                          value={tier.color_code || ''}
                          onChange={(value) =>
                            updateTier(index, 'color_code', value)
                          }
                          options={COLOR_CODES}
                          placeholder="Select Color"
                          allowEmpty
                          emptyLabel="No Color"
                        />
                        <RemoveTierButtonWrapper>
                          <ButtonV2
                            danger
                            variant="icon"
                            type="button"
                            onClick={() => removeTier(index)}
                            title="Remove tier"
                            icon={<XIcon />}
                          />
                        </RemoveTierButtonWrapper>
                      </TierRow>
                    ))}
                  </>
                )}
              </TierSection>
            )}

            {/* Sales Windows Toggle - only if event has sales windows enabled */}
            {event?.sales_windows && (
              <TwoColumnGrid style={{ marginTop: '24px' }}>
                <ToggleCard>
                  <ToggleInfo>
                    <ToggleTitle>Sales Windows</ToggleTitle>
                    <ToggleDescription>
                      Enable time-based pricing for this ticket
                    </ToggleDescription>
                  </ToggleInfo>
                  <Toggle
                    checked={formData.has_sales_windows}
                    onChange={(e) =>
                      handleChange('has_sales_windows', e.target.checked)
                    }
                  />
                </ToggleCard>
              </TwoColumnGrid>
            )}

            {/* Sales Windows */}
            {formData.has_sales_windows && (
              <>
                <SectionTitle style={{ marginTop: '24px' }}>
                  Sales Windows
                </SectionTitle>
                <SectionDescription>
                  Create promotional pricing periods. Each window ends at the
                  specified time relative to the event date.
                </SectionDescription>

                <SalesWindowsContainer>
                  {formData.windows.length === 0 ? (
                    <EmptyWindowsMessage>
                      No sales windows configured. Add one to enable time-based
                      pricing.
                    </EmptyWindowsMessage>
                  ) : (
                    formData.windows.map((window, windowIndex) => (
                      <SalesWindowCard key={window.id}>
                        <SalesWindowHeader>
                          <SalesWindowTitle>
                            {window.name || `Window ${windowIndex + 1}`}
                          </SalesWindowTitle>
                          <SmallIconButtonWrapper>
                            <ButtonV2
                              danger
                              variant="icon"
                              type="button"
                              onClick={() => removeSalesWindow(windowIndex)}
                              title="Remove window"
                              icon={<XIcon />}
                            />
                          </SmallIconButtonWrapper>
                        </SalesWindowHeader>

                        <FormRow columns="2fr 1fr 130px 120px">
                          <FormGroup>
                            <Label>Window Name</Label>
                            <Input
                              type="text"
                              placeholder="e.g., Early Bird"
                              value={window.name}
                              onChange={(e) =>
                                updateSalesWindow(
                                  windowIndex,
                                  'name',
                                  e.target.value
                                )
                              }
                            />
                          </FormGroup>

                          <FormGroup>
                            <Label>End Date</Label>
                            <DateTimePicker
                              value={window.ends}
                              onChange={(value) =>
                                updateSalesWindow(windowIndex, 'ends', value)
                              }
                              showTime={false}
                            />
                          </FormGroup>

                          <FormGroup>
                            <Label>End Time</Label>
                            <DateTimePicker
                              value={window.time || '23:59'}
                              onChange={(time) =>
                                updateSalesWindow(windowIndex, 'time', time)
                              }
                              timeOnly
                            />
                          </FormGroup>

                          {!formData.reserved_seat && (
                            <FormGroup>
                              <Label>Price</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="0.00"
                                value={window.price}
                                onChange={(e) =>
                                  updateSalesWindow(
                                    windowIndex,
                                    'price',
                                    e.target.value
                                  )
                                }
                              />
                            </FormGroup>
                          )}
                        </FormRow>

                        {/* Tiers inside window - only for reserved seats */}
                        {formData.reserved_seat && (
                          <TierSection style={{ marginTop: '16px' }}>
                            <TierHeader>
                              <TierTitle style={{ fontSize: '13px' }}>
                                Pricing Tiers
                              </TierTitle>
                              <AddTierButtonWrapper>
                                <ButtonV2
                                  elevated
                                  type="button"
                                  onClick={() => addTierToWindow(windowIndex)}
                                  icon={<PlusIcon />}
                                >
                                  Add
                                </ButtonV2>
                              </AddTierButtonWrapper>
                            </TierHeader>

                            {window.tiers.length === 0 ? (
                              <EmptyTiersMessage>
                                No tiers. Add tiers for seat-based pricing.
                              </EmptyTiersMessage>
                            ) : (
                              <>
                                <TierRow>
                                  <Label>Label</Label>
                                  <Label>App Price</Label>
                                  <Label>Track Price</Label>
                                  <Label>Color</Label>
                                  <div />
                                </TierRow>
                                {window.tiers.map((tier, tierIndex) => (
                                  <TierRow key={tier.id}>
                                    <Input
                                      type="text"
                                      placeholder="e.g., Front Row"
                                      value={tier.name}
                                      onChange={(e) =>
                                        updateTierInWindow(
                                          windowIndex,
                                          tierIndex,
                                          'name',
                                          e.target.value
                                        )
                                      }
                                    />
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      placeholder="0.00"
                                      value={tier.price}
                                      onChange={(e) =>
                                        updateTierInWindow(
                                          windowIndex,
                                          tierIndex,
                                          'price',
                                          e.target.value
                                        )
                                      }
                                    />
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      placeholder="0.00"
                                      value={tier.track_price}
                                      onChange={(e) =>
                                        updateTierInWindow(
                                          windowIndex,
                                          tierIndex,
                                          'track_price',
                                          e.target.value
                                        )
                                      }
                                    />
                                    <ColorSelect
                                      value={tier.color_code || ''}
                                      onChange={(value) =>
                                        updateTierInWindow(
                                          windowIndex,
                                          tierIndex,
                                          'color_code',
                                          value
                                        )
                                      }
                                      options={COLOR_CODES}
                                      placeholder="Select Color"
                                      allowEmpty
                                      emptyLabel="No Color"
                                    />
                                    <RemoveTierButtonWrapper>
                                      <ButtonV2
                                        danger
                                        variant="icon"
                                        type="button"
                                        onClick={() =>
                                          removeTierFromWindow(
                                            windowIndex,
                                            tierIndex
                                          )
                                        }
                                        title="Remove tier"
                                        icon={<XIcon />}
                                      />
                                    </RemoveTierButtonWrapper>
                                  </TierRow>
                                ))}
                              </>
                            )}
                          </TierSection>
                        )}
                      </SalesWindowCard>
                    ))
                  )}

                  <AddWindowButtonWrapper>
                    <ButtonV2
                      type="button"
                      onClick={addSalesWindow}
                      icon={<PlusIcon />}
                    >
                      Add Sales Window
                    </ButtonV2>
                  </AddWindowButtonWrapper>
                </SalesWindowsContainer>
              </>
            )}
          </FormSection>
        </FormCard>

        {/* Options */}
        <FormCard>
          <FormSection>
            <SectionTitle>Additional Options</SectionTitle>

            <FormRow columns="1fr 1fr 1fr">
              <FormFieldSelect
                label="Visibility"
                value={formData.visibility}
                onChange={(e) => handleChange('visibility', e.target.value)}
                options={VISIBILITY_OPTIONS}
              />

              <FormGroup>
                <FormFieldSelect
                  label="Category"
                  value={formData.category_id}
                  onChange={(e) => handleChange('category_id', e.target.value)}
                  placeholder="Select Category"
                  options={categoryOptions}
                />
                {errors.category_id && (
                  <ErrorText>{errors.category_id}</ErrorText>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Ticket Limit</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="No limit"
                  value={formData.limit}
                  onChange={(e) => handleChange('limit', e.target.value)}
                />
              </FormGroup>
            </FormRow>

            {/* Featured Ticket */}
            <ToggleRow style={{ marginTop: '16px' }}>
              <ToggleInfo>
                <ToggleTitle>Featured Ticket</ToggleTitle>
                <ToggleDescription>
                  Highlight this ticket with custom text
                </ToggleDescription>
              </ToggleInfo>
              <Toggle
                checked={formData.is_featured}
                onChange={(e) => handleChange('is_featured', e.target.checked)}
              />
            </ToggleRow>

            {formData.is_featured && (
              <FormGroup style={{ marginTop: '16px' }}>
                <Label>Featured Text</Label>
                <Input
                  type="text"
                  placeholder="e.g., Best Value!"
                  value={formData.featured_text}
                  onChange={(e) =>
                    handleChange('featured_text', e.target.value)
                  }
                />
              </FormGroup>
            )}

            {/* Reserved Seating Options */}
            {formData.reserved_seat && (
              <>
                <SectionTitle style={{ marginTop: '24px' }}>
                  Reserved Seating Settings
                </SectionTitle>

                <FormRow columns="1fr 1fr">
                  <FormGroup>
                    <Label>Seats.io Category</Label>
                    <Input
                      type="text"
                      placeholder="Enter category"
                      value={formData.reserved_seat_category}
                      onChange={(e) =>
                        handleChange('reserved_seat_category', e.target.value)
                      }
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Ticket Prefix</Label>
                    <Input
                      type="text"
                      placeholder="Enter prefix"
                      value={formData.reserved_seat_prefix}
                      onChange={(e) =>
                        handleChange('reserved_seat_prefix', e.target.value)
                      }
                    />
                  </FormGroup>
                </FormRow>

                <TwoColumnGrid style={{ marginTop: '16px', marginBottom: 0 }}>
                  <FormGroup>
                    <Label>Seating Chart</Label>
                    <SearchableSelect
                      value={formData.seating_chart_id}
                      onChange={(option) =>
                        handleChange('seating_chart_id', option?.value || null)
                      }
                      options={seatingChartOptions}
                      loading={chartsLoading}
                      placeholder="Select a seating chart..."
                      isClearable
                    />
                  </FormGroup>

                  <ToggleRow>
                    <ToggleInfo>
                      <ToggleTitle>Show as Section/Row/Seat</ToggleTitle>
                      <ToggleDescription>
                        Display seat information in section, row, seat format
                      </ToggleDescription>
                    </ToggleInfo>
                    <Toggle
                      checked={formData.reserved_seat_section}
                      onChange={(e) =>
                        handleChange('reserved_seat_section', e.target.checked)
                      }
                    />
                  </ToggleRow>
                </TwoColumnGrid>
              </>
            )}

            {/* Member Numbers - Only for registration types */}
            {isRegistration && (
              <>
                <SectionTitle style={{ marginTop: '24px' }}>
                  Member Numbers
                </SectionTitle>
                <SectionDescription>
                  Configure automatic member number assignment for purchasers
                </SectionDescription>

                <ToggleRow>
                  <ToggleInfo>
                    <ToggleTitle>Set Member Numbers</ToggleTitle>
                    <ToggleDescription>
                      Automatically assign member numbers to purchasers
                    </ToggleDescription>
                  </ToggleInfo>
                  <Toggle
                    checked={formData.set_member_number}
                    onChange={(e) =>
                      handleChange('set_member_number', e.target.checked)
                    }
                  />
                </ToggleRow>

                {formData.set_member_number && (
                  <>
                    <FormRow columns="1fr 1fr">
                      <FormGroup>
                        <LabelWithTooltip>
                          Prefix Text
                          <Tooltip
                            info
                            content="Text that appears before the member number. For example, entering '2026' would produce member numbers like '2026-1'."
                            iconColor={infoIconColor}
                            iconSize="16px"
                          />
                        </LabelWithTooltip>
                        <Input
                          type="text"
                          value={formData.prefix_text}
                          onChange={(e) =>
                            handleChange('prefix_text', e.target.value)
                          }
                          placeholder="e.g., 2026"
                        />
                      </FormGroup>

                      <FormGroup>
                        <LabelWithTooltip>
                          Suffix Text
                          <Tooltip
                            info
                            content="Text that appears after the member number. For example, entering 'D' would produce member numbers like '1-D'."
                            iconColor={infoIconColor}
                            iconSize="16px"
                          />
                        </LabelWithTooltip>
                        <Input
                          type="text"
                          value={formData.suffix_text}
                          onChange={(e) =>
                            handleChange('suffix_text', e.target.value)
                          }
                          placeholder="e.g., D"
                        />
                      </FormGroup>
                    </FormRow>

                    <FormRow>
                      <FormGroup>
                        <LabelWithTooltip>
                          Starting Member Number
                          <Tooltip
                            info
                            content="The next member number to be assigned. Numbers will increment from this value."
                            iconColor={infoIconColor}
                            iconSize="16px"
                          />
                        </LabelWithTooltip>
                        <Input
                          type="number"
                          value={formData.last_member_number}
                          onChange={(e) =>
                            handleChange('last_member_number', e.target.value)
                          }
                          placeholder="1"
                          min="1"
                        />
                      </FormGroup>
                    </FormRow>

                    <SectionDescription style={{ marginTop: '16px' }}>
                      <strong>Preview:</strong>{' '}
                      {[
                        formData.prefix_text,
                        formData.last_member_number || '1',
                        formData.suffix_text,
                      ]
                        .filter(Boolean)
                        .join('-')}
                    </SectionDescription>
                  </>
                )}
              </>
            )}
          </FormSection>
        </FormCard>

        {/* Registration Pages (only for registration tickets) */}
        {isRegistration && (
          <FormCard>
            <FormSection>
              <SectionTitle>Registration Pages</SectionTitle>
              <SectionDescription>
                Add pages that will be displayed during checkout. Pages can
                include PDFs, videos, forms, and waivers.
              </SectionDescription>

              <PagesContainer>
                <PagesListSection>
                  {formData.pages.length === 0 ? (
                    <EmptyPagesMessage>
                      No pages configured yet. Add a page to get started.
                    </EmptyPagesMessage>
                  ) : (
                    formData.pages.map((page, pageIndex) => (
                      <PageCard key={page.id}>
                        <PageCardHeader>
                          <PageCardTitle>
                            {page.name || `Page ${pageIndex + 1}`}
                          </PageCardTitle>
                          <DeletePageButtonWrapper>
                            <ButtonV2
                              danger
                              variant="icon"
                              type="button"
                              onClick={() => removePage(pageIndex)}
                              title="Remove page"
                              icon={<TrashIcon />}
                            />
                          </DeletePageButtonWrapper>
                        </PageCardHeader>

                        <FormRow columns="1fr">
                          <FormGroup>
                            <Label>Page Name</Label>
                            <Input
                              type="text"
                              placeholder="Enter page name"
                              value={page.name || ''}
                              onChange={(e) =>
                                updatePage(pageIndex, 'name', e.target.value)
                              }
                            />
                          </FormGroup>
                        </FormRow>

                        <FormGroup style={{ marginBottom: '16px' }}>
                          <Label>PDF Upload</Label>
                          <AttachmentUpload
                            attachments={
                              page.pdf_id &&
                              (page.pdf_url || page.pdf_upload_url)
                                ? [
                                    {
                                      id: page.pdf_id,
                                      name: 'Uploaded PDF',
                                      size: 0,
                                      sizeFormatted: '',
                                      url: page.pdf_url || page.pdf_upload_url,
                                      type: 'application/pdf',
                                      isPDF: true,
                                    },
                                  ]
                                : []
                            }
                            onAttachmentsChange={(attachments) => {
                              if (attachments.length > 0) {
                                updatePage(
                                  pageIndex,
                                  'pdf_id',
                                  attachments[0].id
                                );
                                updatePage(
                                  pageIndex,
                                  'pdf_url',
                                  attachments[0].url
                                );
                              } else {
                                updatePage(pageIndex, 'pdf_id', null);
                                updatePage(pageIndex, 'pdf_url', null);
                              }
                            }}
                            maxFiles={1}
                            maxFileSize={10485760}
                            channel="SMS"
                          />
                        </FormGroup>

                        <FormRow columns="1fr 1fr">
                          <FormGroup>
                            <Label>Video URL</Label>
                            <Input
                              type="url"
                              placeholder="https://..."
                              value={page.video_url || ''}
                              onChange={(e) =>
                                updatePage(
                                  pageIndex,
                                  'video_url',
                                  e.target.value
                                )
                              }
                            />
                          </FormGroup>

                          <FormGroup>
                            <Label>Form ID</Label>
                            <SearchableSelect
                              value={
                                page.form_id ? parseInt(page.form_id) : null
                              }
                              onChange={(option) => {
                                updatePage(
                                  pageIndex,
                                  'form_id',
                                  option?.value?.toString() || null
                                );
                              }}
                              options={formstackOptions}
                              loading={formstackLoading}
                              onSearch={handleFormstackSearch}
                              minSearchLength={4}
                              placeholder="Type to search forms..."
                              noResultsText="No forms found"
                            />
                          </FormGroup>
                        </FormRow>

                        <FormGroup style={{ marginTop: '16px' }}>
                          <Label>Waivers</Label>
                          <WaiversContainer>
                            {(page.waivers || []).length === 0 ? (
                              <EmptyText>No waivers added</EmptyText>
                            ) : (
                              page.waivers.map((waiver, waiverIndex) => (
                                <WaiverRow key={waiverIndex}>
                                  <FormFieldSelect
                                    value={waiver.waiver || ''}
                                    onChange={(e) =>
                                      updateWaiverInPage(
                                        pageIndex,
                                        waiverIndex,
                                        e.target.value
                                      )
                                    }
                                    placeholder="Select a waiver..."
                                    options={waivers.map((w) => ({
                                      label: w.name,
                                      value: w.id,
                                    }))}
                                  />
                                  <RemoveWaiverButtonWrapper>
                                    <ButtonV2
                                      danger
                                      variant="icon"
                                      type="button"
                                      onClick={() =>
                                        removeWaiverFromPage(
                                          pageIndex,
                                          waiverIndex
                                        )
                                      }
                                      title="Remove waiver"
                                      icon={<MinusIcon />}
                                    />
                                  </RemoveWaiverButtonWrapper>
                                </WaiverRow>
                              ))
                            )}
                            <AddWaiverButtonWrapper>
                              <ButtonV2
                                elevated
                                type="button"
                                onClick={() => addWaiverToPage(pageIndex)}
                                icon={<PlusIcon />}
                              >
                                Add another waiver
                              </ButtonV2>
                            </AddWaiverButtonWrapper>
                          </WaiversContainer>
                        </FormGroup>
                      </PageCard>
                    ))
                  )}

                  <AddPageButtonWrapper>
                    <ButtonV2
                      elevated
                      type="button"
                      onClick={addPage}
                      icon={<PlusIcon />}
                    >
                      {formData.pages.length > 0
                        ? 'ADD ANOTHER PAGE'
                        : 'ADD PAGE'}
                    </ButtonV2>
                  </AddPageButtonWrapper>
                </PagesListSection>

                {formData.pages_with_order.length > 0 && (
                  <PagesOrderSection>
                    <OrderSectionTitle>Pages Order In App</OrderSectionTitle>
                    <ReactSortable
                      list={getOrderedItems(formData.pages_with_order)}
                      setList={handlePagesSort}
                    >
                      {getOrderedItems(formData.pages_with_order).map(
                        (page) => (
                          <DraggablePageItem key={page.id || page.order}>
                            <DragHandle>
                              <DragHandleIcon />
                            </DragHandle>
                            <PageOrderName>
                              {page.name || 'Unnamed Page'}
                            </PageOrderName>
                          </DraggablePageItem>
                        )
                      )}
                    </ReactSortable>
                  </PagesOrderSection>
                )}
              </PagesContainer>
            </FormSection>
          </FormCard>
        )}
      </Container>

      {/* Custom fee confirmation - shown when changing type or reserved
          seating would overwrite fees that don't match the current defaults */}
      {pendingFeeReset &&
        (() => {
          const isTypeChange = pendingFeeReset.field === 'type';
          const newDefaults = getDefaultTicketFees(
            formData.price,
            isTypeChange ? pendingFeeReset.value : formData.type,
            isTypeChange ? formData.reserved_seat : pendingFeeReset.value
          );
          const newTypeLabel = isTypeChange
            ? TICKET_TYPES.find((t) => t.value === pendingFeeReset.value)
                ?.label || pendingFeeReset.value
            : '';

          return (
            <ConfirmModalOverlay onClick={() => setPendingFeeReset(null)}>
              <ConfirmModalContent onClick={(e) => e.stopPropagation()}>
                <ConfirmModalIcon>
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </ConfirmModalIcon>
                <ConfirmModalTitle>Custom Fees Detected</ConfirmModalTitle>
                <ConfirmModalText>
                  This ticket&apos;s fees (
                  <ConfirmModalHighlight>
                    {formatFee(
                      parseFloat(formData.fee_dollar) || 0,
                      parseFloat(formData.fee_percent) || 0
                    )}
                  </ConfirmModalHighlight>
                  ) don&apos;t match the standard default for its current
                  settings.{' '}
                  {isTypeChange ? (
                    <>
                      Changing the type to{' '}
                      <ConfirmModalHighlight>
                        {newTypeLabel}
                      </ConfirmModalHighlight>
                    </>
                  ) : (
                    <>
                      Turning reserved seating{' '}
                      <ConfirmModalHighlight>
                        {pendingFeeReset.value ? 'on' : 'off'}
                      </ConfirmModalHighlight>
                    </>
                  )}{' '}
                  normally resets the fees
                  {newDefaults.fee_dollar !== undefined && (
                    <>
                      {' '}
                      to{' '}
                      <ConfirmModalHighlight>
                        {formatFee(
                          newDefaults.fee_dollar,
                          newDefaults.fee_percent
                        )}
                      </ConfirmModalHighlight>
                    </>
                  )}
                  .
                </ConfirmModalText>
                <ConfirmModalActions>
                  <ConfirmModalButton onClick={handleKeepCustomFees}>
                    Keep Custom Fees
                  </ConfirmModalButton>
                  <ConfirmModalButton
                    variant="primary"
                    onClick={handleUseDefaultFees}
                  >
                    Use Default Fees
                  </ConfirmModalButton>
                </ConfirmModalActions>
              </ConfirmModalContent>
            </ConfirmModalOverlay>
          );
        })()}

      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ isOpen: false })}
        type={toaster.type}
        title={toaster.title}
        message={toaster.message}
        duration={5000}
      />
    </PageWrapper>
  );
};

// Main component wrapped with theme provider
const EditEventTicket = () => {
  return (
    <EnhancedThemeProvider>
      <EditEventTicketContent />
    </EnhancedThemeProvider>
  );
};

export default EditEventTicket;
